import type { Task, TaskRiskLevel, TaskStatus, TaskType } from "../../types/domain";
import { IcoClock } from "../../components/icons";
import { isTouchLike } from "../../lib/device";
import { parseIsoLocal, todayIsoLocal } from "../../lib/weekDates";

const STATUS_LABEL: Record<TaskStatus, string> = {
  planned: "Запланировано",
  in_progress: "В работе",
  done: "Готово",
  cancelled: "Отменено",
};

const RISK_LABEL: Record<TaskRiskLevel, string> = {
  none: "",
  risk: "Риск",
  critical: "Критично",
};

const MONTH_GENITIVE = [
  "янв", "фев", "мар", "апр", "мая", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

function formatDueLabel(dueDate: string, dueTime: string | null): string {
  const d = parseIsoLocal(dueDate);
  const day = d.getDate();
  const month = MONTH_GENITIVE[d.getMonth()] ?? "";
  const head = `до ${day} ${month}`;
  return dueTime ? `${head}, ${dueTime}` : head;
}

export function isOverdue(task: Task): boolean {
  if (!task.dueDate) return false;
  if (task.status === "done" || task.status === "cancelled") return false;
  return task.dueDate < todayIsoLocal();
}

interface TaskCardProps {
  task: Task;
  type: TaskType | null;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
  draggable?: boolean;
  /** Цвет запуска, к которому относится задача. Подсвечивает левую полоску
   *  карточки, если у задачи нет проблем (риск/просрочка), чтобы не перебивать
   *  важные сигналы. */
  launchAccent?: string | null;
}

export function TaskCard({
  task,
  type,
  onOpen,
  onDragStart,
  onDragEnd,
  isDragging,
  draggable = true,
  launchAccent = null,
}: TaskCardProps) {
  const overdue = isOverdue(task);
  const isCritical = task.riskLevel === "critical";
  const isRisk = task.riskLevel === "risk";
  const noAssignee = !task.assigneeName;
  const noPlanned = !task.plannedDate;

  const showDueBadge =
    !!task.dueDate && (task.plannedDate !== task.dueDate || overdue);

  const plannedAfterDue =
    !!task.plannedDate && !!task.dueDate && task.plannedDate > task.dueDate;

  const typeColor = type?.color ?? "#94A3B8";
  const typeBg = type?.bg ?? "#F1F5F9";
  const typeText = type?.text ?? "#475569";
  const typeName = type?.name ?? "Тип";

  const baseAccent = launchAccent || typeColor;
  const accent =
    isCritical || overdue || plannedAfterDue
      ? "#DC2626"
      : isRisk
      ? "#F59E0B"
      : baseAccent;

  const style = {
    "--card-accent": accent,
    "--type-color": typeColor,
    "--type-bg": typeBg,
    "--type-text": typeText,
    cursor: draggable ? "pointer" : "default",
  } as React.CSSProperties;

  const className =
    "card task" +
    (isDragging ? " dragging" : "") +
    ` task-status-${task.status}` +
    (isCritical || plannedAfterDue ? " task-critical" : "") +
    (overdue ? " task-overdue" : "");

  const canDrag = draggable && !isTouchLike();
  const previewText = task.description ? task.description.replace(/\s+/g, " ").trim() : "";

  return (
    <div
      className={className}
      style={style}
      onClick={onOpen}
      draggable={canDrag}
      onDragStart={
        canDrag
          ? (e) => {
              e.dataTransfer.effectAllowed = "move";
              try {
                e.dataTransfer.setData("application/x-launchlab-task", task.id);
              } catch {
                // ignore
              }
              onDragStart();
            }
          : undefined
      }
      onDragEnd={canDrag ? onDragEnd : undefined}
      data-task-id={task.id}
    >
      <div className="card-head">
        <span className="type-badge">
          <span className="type-dot" />
          {typeName}
        </span>
        {showDueBadge && (
          <span
            className={
              "task-due-pill" +
              (overdue ? " is-overdue" : "") +
              (plannedAfterDue ? " is-after-due" : "")
            }
            title={
              overdue
                ? "Дедлайн прошёл"
                : plannedAfterDue
                ? "План стоит после дедлайна"
                : "Крайний срок"
            }
          >
            <IcoClock size={11} />
            {formatDueLabel(task.dueDate!, task.dueTime ?? null)}
          </span>
        )}
      </div>
      <h3 className="card-title">{task.title}</h3>
      {previewText && <p className="card-preview">{previewText}</p>}

      {(task.recurrenceId || overdue || isCritical || isRisk || noPlanned || noAssignee) && (
        <div className="task-badges">
          {task.recurrenceId && <span className="task-badge task-badge-repeat">Повтор</span>}
          {overdue && <span className="task-badge task-badge-overdue">Просрочено</span>}
          {isCritical && <span className="task-badge task-badge-critical">{RISK_LABEL.critical}</span>}
          {isRisk && !isCritical && (
            <span className="task-badge task-badge-risk">{RISK_LABEL.risk}</span>
          )}
          {noPlanned && <span className="task-badge task-badge-nodate">Без даты</span>}
          {noAssignee && <span className="task-badge task-badge-noassign">Без ответственного</span>}
        </div>
      )}

      <div className="card-foot">
        <span className={"card-status task-status-pill task-status-pill-" + task.status}>
          <span className="status-dot" />
          {STATUS_LABEL[task.status]}
        </span>
        <span className="card-foot-right">
          {task.assigneeName && (
            <span className="task-assignee" title={task.assigneeName}>
              {task.assigneeName}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
